import React from 'react';
import {
  ArrowRight,
  Check,
  Layers3,
  MapPin,
  Pencil,
  Play,
  Route,
  Trash2,
  UsersRound,
} from 'lucide-react';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Checkbox } from '@/shared/ui/checkbox';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/shared/ui/card';
import { Progress } from '@/shared/ui/progress';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/ui/table';
import { cn } from '@/shared/utils/utils';
import type {
  PunchItemWorkflow,
  PunchListStatus,
  PunchWorkflowBatch,
  PunchWorkflowRun,
  WorkflowAreaTagLocation,
} from '../types';
import { formatUnitLabel, PRIORITY_LABELS, STATUS_LABELS } from '../types';
import { workflowRunLocationLabel } from '../utils/workflowLocation';

export type WorkflowConcept = 'job-board' | 'crew-handoffs' | 'sequence-view';

export interface WorkflowConceptCopy {
  name: string;
  shortDescription: string;
  pageTitle: string;
  pageDescription: string;
  runsTab: string;
  templatesTab: string;
  createRun: string;
  createTemplate: string;
}

export const workflowConcepts: Record<WorkflowConcept, WorkflowConceptCopy> = {
  'job-board': {
    name: 'Job board',
    shortDescription: 'Quick progress overview',
    pageTitle: 'Workflows',
    pageDescription: 'Keep multi-trade workflows moving in the right order, at one location or many.',
    runsTab: 'Workflows',
    templatesTab: 'Templates',
    createRun: 'New Workflow',
    createTemplate: 'New Template',
  },
  'crew-handoffs': {
    name: 'Crew view',
    shortDescription: 'Organized by who is up',
    pageTitle: 'Workflows',
    pageDescription: 'See which crew is working now, where they are needed, and who comes next.',
    runsTab: 'Workflows',
    templatesTab: 'Templates',
    createRun: 'New Workflow',
    createTemplate: 'New Template',
  },
  'sequence-view': {
    name: 'Sequence view',
    shortDescription: 'Every handoff in order',
    pageTitle: 'Workflows',
    pageDescription: 'Follow each workflow from the first visit through the final sign-off.',
    runsTab: 'Workflows',
    templatesTab: 'Templates',
    createRun: 'New Workflow',
    createTemplate: 'New Template',
  },
};

const conceptOrder = Object.keys(workflowConcepts) as WorkflowConcept[];
const terminalStatuses = new Set(['COMPLETED', 'CLOSED']);

const formatDate = (value: string) => new Date(value).toLocaleDateString(undefined, {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
});

const progressFor = ({ completed_steps: complete, total_steps: total }: Pick<PunchWorkflowRun | PunchWorkflowBatch, 'completed_steps' | 'total_steps'>) => (
  total > 0 ? (complete / total) * 100 : 0
);

const activeItemsFor = (run: PunchWorkflowRun) => run.items.filter(
  (item) => item.workflow_stage === run.current_stage && !terminalStatuses.has(item.status),
);

const ownerFor = (item: PunchWorkflowRun['items'][number]) => (
  item.responsible_trade_partner?.company_name ?? item.trade?.name ?? 'Unassigned crew'
);

const itemStatusClass: Record<PunchListStatus, string> = {
  OPEN: 'border-transparent bg-primary/15 text-primary',
  PENDING_APPROVAL: 'border-transparent bg-amber-500/15 text-amber-700 dark:text-amber-300',
  COMPLETED: 'border-transparent bg-success/15 text-success',
  OVERDUE: 'border-transparent bg-destructive/15 text-destructive',
  CLOSED: 'border-transparent bg-muted text-muted-foreground',
};

const openItemsFirst = (
  left: { item: PunchWorkflowRun['items'][number] },
  right: { item: PunchWorkflowRun['items'][number] },
) => Number(right.item.status === 'OPEN') - Number(left.item.status === 'OPEN');

const groupedStages = (definition: PunchWorkflowRun['definition']) => {
  const stages = new Map<number, PunchWorkflowRun['definition']>();
  definition.forEach((step) => stages.set(step.stage, [...(stages.get(step.stage) ?? []), step]));
  return [...stages.entries()].sort(([left], [right]) => left - right);
};

const groupedTemplateStages = (workflow: PunchItemWorkflow) => {
  const stages = new Map<number, PunchItemWorkflow['steps']>();
  workflow.steps.forEach((step) => stages.set(step.stage, [...(stages.get(step.stage) ?? []), step]));
  return [...stages.entries()].sort(([left], [right]) => left - right);
};

export const WorkflowConceptSwitcher: React.FC<{
  value: WorkflowConcept;
  onChange: (value: WorkflowConcept) => void;
}> = ({ value, onChange }) => (
  <section aria-label="Choose a workflow design" className="rounded-[1.375rem] border border-border bg-card p-2 shadow-slate">
    <div className="grid gap-1.5 md:grid-cols-3">
      {conceptOrder.map((concept, index) => {
        const option = workflowConcepts[concept];
        const selected = concept === value;
        return (
          <button
            key={concept}
            type="button"
            aria-pressed={selected}
            onClick={() => onChange(concept)}
            className={cn(
              'flex items-center gap-3 rounded-[13px] px-4 py-3 text-left transition-colors duration-150',
              selected
                ? 'bg-primary/12 text-foreground ring-1 ring-primary/30'
                : 'text-muted-foreground hover:bg-accent hover:text-foreground',
            )}
          >
            <span className={cn(
              'grid size-8 shrink-0 place-items-center rounded-[10px] text-xs font-bold',
              selected ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground',
            )}>
              {index + 1}
            </span>
            <span className="min-w-0">
              <span className="block text-sm font-bold">{option.name}</span>
              <span className="block truncate text-xs text-muted-foreground">{option.shortDescription}</span>
            </span>
          </button>
        );
      })}
    </div>
  </section>
);

interface RunsViewProps {
  concept: WorkflowConcept;
  runs: PunchWorkflowRun[];
  batches: PunchWorkflowBatch[];
  areaTags?: WorkflowAreaTagLocation[];
  onOpenItem: (id: string) => void;
  selectedItemIds?: ReadonlySet<string>;
  onSelectItem?: (id: string, checked: boolean) => void;
  onSelectWorkflow?: (ids: string[], checked: boolean) => void;
}

const EmptyRuns: React.FC = () => (
  <Card className="border-dashed">
    <CardContent className="flex flex-col items-center px-6 py-14 text-center">
      <Route className="size-9 text-primary" strokeWidth={1.8} />
      <h2 className="mt-4 font-display text-xl font-bold">No workflows yet</h2>
      <p className="mt-2 max-w-md text-sm text-muted-foreground">
        Use the action at the top of the page to lay out the work, choose a location, and assign the first crew.
      </p>
    </CardContent>
  </Card>
);

const JobBoardRuns: React.FC<Omit<RunsViewProps, 'concept'>> = ({
  runs,
  batches,
  areaTags = [],
  onOpenItem,
  selectedItemIds = new Set<string>(),
  onSelectItem,
  onSelectWorkflow,
}) => {
  const unbatchedRuns = runs.filter((run) => !run.batch);
  const groups = [
    ...batches.map((batch) => ({
      id: batch.id,
      name: batch.name,
      kind: 'batch' as const,
      status: batch.status,
      runs: batch.runs,
      completedSteps: batch.completed_steps,
      totalSteps: batch.total_steps,
    })),
    ...unbatchedRuns.map((run) => ({
      id: run.id,
      name: run.workflow_name,
      kind: 'single' as const,
      status: run.status,
      runs: [run],
      completedSteps: run.completed_steps,
      totalSteps: run.total_steps,
    })),
  ];

  if (runs.length === 0 && batches.length === 0) return <EmptyRuns />;

  return (
    <section className="space-y-3">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-muted-foreground">Punch items by workflow</p>
          <p className="mt-1 text-sm text-muted-foreground">Every created punch item, its current status, and the company responsible.</p>
        </div>
        <Badge variant="outline">
          {groups.reduce((count, group) => count + group.runs.reduce((sum, run) => sum + run.items.length, 0), 0)} punch items
        </Badge>
      </div>

      <Table className="min-w-[980px]">
        <TableHeader className="bg-card">
          <TableRow className="hover:bg-transparent">
            <TableHead className="w-12 px-3"><span className="sr-only">Select</span></TableHead>
            <TableHead className="w-[190px]">Location</TableHead>
            <TableHead className="min-w-[280px]">Punch item</TableHead>
            <TableHead className="w-[150px]">Status</TableHead>
            <TableHead className="w-[160px]">Trade</TableHead>
            <TableHead className="w-[220px]">Trade Partner</TableHead>
            <TableHead className="w-[130px]">Handoff</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {groups.map((group) => {
            const items = group.runs
              .flatMap((run) => run.items.map((item) => ({ run, item })))
              .sort(openItemsFirst);
            const percent = group.totalSteps > 0
              ? (group.completedSteps / group.totalSteps) * 100
              : 0;
            return (
              <React.Fragment key={`${group.kind}-${group.id}`}>
                <TableRow className="border-y border-border bg-muted/55 hover:bg-muted/55">
                  <TableCell colSpan={7} className="px-3 py-3">
                    <div className="flex flex-wrap items-center justify-between gap-4">
                      <div className="flex min-w-0 items-center gap-3">
                        <Checkbox
                          aria-label={`Select workflow ${group.name}`}
                          checked={
                            items.length > 0 && items.every(({ item }) => selectedItemIds.has(item.id))
                              ? true
                              : items.some(({ item }) => selectedItemIds.has(item.id))
                                ? 'indeterminate'
                                : false
                          }
                          disabled={items.length === 0 || !onSelectWorkflow}
                          onCheckedChange={(checked) => onSelectWorkflow?.(
                            items.map(({ item }) => item.id),
                            checked === true,
                          )}
                        />
                        <span className="grid size-9 shrink-0 place-items-center rounded-[10px] border border-primary/20 bg-primary/12 text-primary">
                          {group.kind === 'batch'
                            ? <Layers3 className="size-4" strokeWidth={1.9} />
                            : <MapPin className="size-4" strokeWidth={1.9} />}
                        </span>
                        <div className="min-w-0">
                          <p className="truncate font-display text-sm font-bold">{group.name}</p>
                          <p className="mt-0.5 text-xs text-muted-foreground">
                            {group.kind === 'batch'
                              ? `Batch · ${group.runs.length} ${group.runs.length === 1 ? 'issue' : 'issues'}`
                              : workflowRunLocationLabel(group.runs[0], areaTags)}
                            {' · '}{items.length} {items.length === 1 ? 'punch item' : 'punch items'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="hidden w-36 sm:block">
                          <Progress value={percent} className="h-1.5" />
                          <p className="mt-1 text-right text-[10px] font-semibold text-muted-foreground">
                            {group.completedSteps} of {group.totalSteps} complete
                          </p>
                        </div>
                        <Badge className={group.status === 'COMPLETED' ? 'bg-success/15 text-success' : 'bg-primary/15 text-primary'}>
                          {group.status === 'COMPLETED' ? 'Finished' : 'In progress'}
                        </Badge>
                      </div>
                    </div>
                  </TableCell>
                </TableRow>

                {items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="py-6 text-center text-sm text-muted-foreground">
                      No punch items have been created for this workflow yet.
                    </TableCell>
                  </TableRow>
                ) : items.map(({ run, item }) => {
                  const current = run.status === 'ACTIVE'
                    && item.workflow_stage === run.current_stage
                    && !terminalStatuses.has(item.status);
                  const tradeName = item.trade?.name
                    ?? item.responsible_trade_partner?.trade?.name
                    ?? 'Not assigned';
                  return (
                    <TableRow
                      key={item.id}
                      tabIndex={0}
                      aria-label={`Open punch item ${item.title}`}
                      onClick={() => onOpenItem(item.id)}
                      onKeyDown={(event) => {
                        if (event.key !== 'Enter' && event.key !== ' ') return;
                        event.preventDefault();
                        onOpenItem(item.id);
                      }}
                      data-state={selectedItemIds.has(item.id) ? 'selected' : undefined}
                      className="group cursor-pointer bg-card outline-none hover:bg-primary/10 focus-visible:bg-primary/10 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring data-[state=selected]:bg-primary/10"
                    >
                      <TableCell className="px-3 py-3">
                        <Checkbox
                          aria-label={`Select ${item.title}`}
                          checked={selectedItemIds.has(item.id)}
                          disabled={!onSelectItem}
                          onClick={(event) => event.stopPropagation()}
                          onCheckedChange={(checked) => onSelectItem?.(item.id, checked === true)}
                        />
                      </TableCell>
                      <TableCell className="py-3 text-xs font-semibold text-muted-foreground">
                        <span className="flex items-center gap-1.5">
                          <MapPin className="size-3.5 shrink-0 text-primary" />
                          {item.location ? formatUnitLabel(item.location) : workflowRunLocationLabel(run, areaTags)}
                        </span>
                      </TableCell>
                      <TableCell className="py-3">
                        <span className="block max-w-[360px] text-left text-sm font-semibold text-foreground transition-colors group-hover:text-primary">
                          {item.title}
                        </span>
                        <p className="mt-1 text-[11px] text-muted-foreground">
                          {PRIORITY_LABELS[item.priority]} priority
                        </p>
                      </TableCell>
                      <TableCell className="py-3">
                        <Badge className={cn('whitespace-nowrap', itemStatusClass[item.status])}>
                          {STATUS_LABELS[item.status]}
                        </Badge>
                      </TableCell>
                      <TableCell className="py-3 text-sm font-medium">
                        {tradeName}
                      </TableCell>
                      <TableCell className="py-3 text-sm text-muted-foreground">
                        {item.responsible_trade_partner?.company_name ?? 'Not assigned'}
                      </TableCell>
                      <TableCell className="py-3">
                        <div className="flex items-center gap-2 whitespace-nowrap text-xs text-muted-foreground">
                          <span>{item.workflow_stage ?? '—'} of {run.total_stages}</span>
                          {current && <Badge variant="outline" className="border-primary/30 text-primary">Current</Badge>}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>
    </section>
  );
};

const CrewHandoffRuns: React.FC<Omit<RunsViewProps, 'concept'>> = ({ runs, batches, onOpenItem }) => {
  const crewAssignments = new Map<string, Array<{ run: PunchWorkflowRun; item: PunchWorkflowRun['items'][number] }>>();
  runs.filter((run) => run.status === 'ACTIVE').forEach((run) => {
    activeItemsFor(run).forEach((item) => {
      const owner = ownerFor(item);
      crewAssignments.set(owner, [...(crewAssignments.get(owner) ?? []), { run, item }]);
    });
  });

  if (runs.length === 0 && batches.length === 0) return <EmptyRuns />;

  return (
    <div className="grid gap-6 xl:grid-cols-[minmax(0,1.35fr)_minmax(280px,0.65fr)]">
      <section className="space-y-3">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-muted-foreground">Crews on deck</p>
          <p className="mt-1 text-sm text-muted-foreground">Assignments are grouped so each contractor can handle their stops together.</p>
        </div>
        <div className="space-y-4">
          {[...crewAssignments.entries()].map(([owner, assignments]) => (
            <Card key={owner}>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <span className="grid size-10 place-items-center rounded-xl bg-primary/12 text-primary">
                      <UsersRound className="size-5" strokeWidth={1.9} />
                    </span>
                    <div>
                      <CardTitle className="text-lg">{owner}</CardTitle>
                      <CardDescription>{assignments.length} current {assignments.length === 1 ? 'assignment' : 'assignments'}</CardDescription>
                    </div>
                  </div>
                  <Badge variant="outline">Working now</Badge>
                </div>
              </CardHeader>
              <CardContent className="grid gap-2 sm:grid-cols-2">
                {assignments.map(({ run, item }) => (
                  <button key={item.id} type="button" onClick={() => onOpenItem(item.id)} className="rounded-xl border border-border bg-muted/40 p-3 text-left transition-colors hover:border-primary hover:bg-primary/10">
                    <span className="block text-sm font-bold">{item.title}</span>
                    <span className="mt-1 flex items-center gap-1.5 text-xs text-muted-foreground">
                      <MapPin className="size-3" /> {formatUnitLabel(run.location)} · {run.workflow_name}
                    </span>
                  </button>
                ))}
              </CardContent>
            </Card>
          ))}
          {crewAssignments.size === 0 && (
            <Card><CardContent className="p-8 text-center text-sm text-muted-foreground">All current assignments are complete.</CardContent></Card>
          )}
        </div>
      </section>

      <aside className="space-y-4">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-muted-foreground">Multi-location workflows</p>
          <p className="mt-1 text-sm text-muted-foreground">Locations waiting on the same crew visit.</p>
        </div>
        {batches.map((batch) => (
          <Card key={batch.id} className="overflow-hidden">
            <CardContent className="p-5">
              <div className="flex items-center justify-between gap-3">
                <Layers3 className="size-5 text-primary" />
                <Badge variant="outline">{batch.total_runs} locations</Badge>
              </div>
              <h3 className="mt-4 font-display text-lg font-bold">{batch.name}</h3>
              <p className="mt-1 text-sm text-muted-foreground">Current crew visit: {batch.current_stage} of {batch.total_stages}</p>
              <Progress value={progressFor(batch)} className="mt-4" />
              <div className="mt-4 flex flex-wrap gap-2">
                {batch.runs.map((run) => (
                  <span key={run.id} className="rounded-full border border-border bg-muted px-2.5 py-1 text-xs font-semibold">
                    {formatUnitLabel(run.location)}
                  </span>
                ))}
              </div>
            </CardContent>
          </Card>
        ))}
        {batches.length === 0 && (
          <Card className="border-dashed"><CardContent className="p-6 text-sm text-muted-foreground">No workflows are currently grouped across locations.</CardContent></Card>
        )}
        <div className="rounded-[1.375rem] border border-border bg-muted/35 p-5">
          <p className="text-sm font-bold">Recently finished</p>
          <p className="mt-1 text-3xl font-display font-extrabold">{runs.filter((run) => run.status === 'COMPLETED').length}</p>
          <p className="mt-1 text-xs text-muted-foreground">workflows have reached final sign-off</p>
        </div>
      </aside>
    </div>
  );
};

const RunTimeline: React.FC<{ run: PunchWorkflowRun; onOpenItem: (id: string) => void }> = ({ run, onOpenItem }) => {
  const itemByStep = new Map(run.items.filter((item) => item.workflow_step_key).map((item) => [item.workflow_step_key, item]));
  return (
    <div className="space-y-0">
      {groupedStages(run.definition).map(([stage, steps], index, groups) => {
        const completed = run.status === 'COMPLETED' || stage < run.current_stage;
        const current = run.status !== 'COMPLETED' && stage === run.current_stage;
        return (
          <div key={stage} className="grid grid-cols-[2rem_minmax(0,1fr)] gap-3">
            <div className="flex flex-col items-center">
              <span className={cn(
                'grid size-8 place-items-center rounded-full border text-xs font-bold',
                completed && 'border-success/30 bg-success/15 text-success',
                current && 'border-primary bg-primary text-primary-foreground ring-4 ring-primary/15',
                !completed && !current && 'border-border bg-muted text-muted-foreground',
              )}>
                {completed ? <Check className="size-4" /> : stage}
              </span>
              {index < groups.length - 1 && <span className={cn('my-1 h-full min-h-8 w-px', completed ? 'bg-success/40' : 'bg-border')} />}
            </div>
            <div className="pb-5">
              <div className="flex items-center gap-2">
                <p className="text-sm font-bold">Visit {stage}</p>
                {current && <Badge className="bg-primary/15 text-primary">Happening now</Badge>}
              </div>
              <div className="mt-2 space-y-1.5">
                {steps.map((step) => {
                  const item = itemByStep.get(step.step_key);
                  const Wrapper = item ? 'button' : 'div';
                  return (
                    <Wrapper
                      key={step.step_key}
                      {...(item ? { type: 'button' as const, onClick: () => onOpenItem(item.id) } : {})}
                      className={cn(
                        'block w-full rounded-lg border border-border bg-muted/35 px-3 py-2 text-left text-sm',
                        item && 'transition-colors hover:border-primary hover:bg-primary/10',
                      )}
                    >
                      <span className="font-semibold">{step.title}</span>
                      {item && <span className="ml-2 text-xs text-muted-foreground">{ownerFor(item)}</span>}
                    </Wrapper>
                  );
                })}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

const SequenceRuns: React.FC<Omit<RunsViewProps, 'concept'>> = ({ runs, batches, onOpenItem }) => {
  const unbatchedRuns = runs.filter((run) => !run.batch);
  if (runs.length === 0 && batches.length === 0) return <EmptyRuns />;

  return (
    <div className="space-y-6">
      {batches.map((batch) => (
        <Card key={batch.id} className="overflow-hidden">
          <CardHeader className="border-b border-border bg-muted/30">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-primary">Workflow batch · {batch.total_runs} locations</p>
                <CardTitle className="mt-2 text-xl">{batch.name}</CardTitle>
                <CardDescription className="mt-1">Every location advances together after the current visit is finished.</CardDescription>
              </div>
              <Badge className={batch.status === 'COMPLETED' ? 'bg-success/15 text-success' : 'bg-primary/15 text-primary'}>
                {batch.completed_steps} of {batch.total_steps} assignments done
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="grid gap-6 p-5 lg:grid-cols-[minmax(0,1fr)_240px]">
            {batch.runs[0] ? <RunTimeline run={batch.runs[0]} onOpenItem={onOpenItem} /> : null}
            <div className="rounded-xl border border-border bg-muted/35 p-4">
              <p className="text-xs font-bold uppercase tracking-[0.12em] text-muted-foreground">Locations in this workflow</p>
              <div className="mt-3 space-y-2">
                {batch.runs.map((run) => (
                  <div key={run.id} className="flex items-center justify-between gap-3 text-sm">
                    <span className="font-semibold">{formatUnitLabel(run.location)}</span>
                    <span className="text-xs text-muted-foreground">{run.completed_steps}/{run.total_steps}</span>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      ))}

      <div className="grid gap-4 xl:grid-cols-2">
        {unbatchedRuns.map((run) => (
          <Card key={run.id}>
            <CardHeader>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <CardTitle className="text-xl">{run.workflow_name}</CardTitle>
                  <CardDescription className="mt-1">{formatUnitLabel(run.location)} · Started {formatDate(run.created_at)}</CardDescription>
                </div>
                <Badge className={run.status === 'COMPLETED' ? 'bg-success/15 text-success' : 'bg-primary/15 text-primary'}>
                  {run.status === 'COMPLETED' ? 'Finished' : 'In progress'}
                </Badge>
              </div>
            </CardHeader>
            <CardContent><RunTimeline run={run} onOpenItem={onOpenItem} /></CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export const WorkflowRunsConceptView: React.FC<RunsViewProps> = (props) => {
  if (props.concept === 'crew-handoffs') return <CrewHandoffRuns {...props} />;
  if (props.concept === 'sequence-view') return <SequenceRuns {...props} />;
  return <JobBoardRuns {...props} />;
};

interface TemplatesViewProps {
  concept: WorkflowConcept;
  templates: PunchItemWorkflow[];
  onLaunch: (workflow: PunchItemWorkflow) => void;
  onBatch: (workflow: PunchItemWorkflow) => void;
  onEdit: (workflow: PunchItemWorkflow) => void;
  onArchive: (workflow: PunchItemWorkflow) => void;
}

const TemplateActions: React.FC<Omit<TemplatesViewProps, 'concept' | 'templates'> & { workflow: PunchItemWorkflow }> = ({
  workflow,
  onLaunch,
  onBatch,
  onEdit,
  onArchive,
}) => (
  <div className="flex flex-wrap gap-2">
    <Button size="sm" onClick={() => onLaunch(workflow)}><Play className="mr-2 size-4" /> Use at one location</Button>
    <Button size="sm" variant="outline" onClick={() => onBatch(workflow)}><Layers3 className="mr-2 size-4" /> Use at several locations</Button>
    <Button size="sm" variant="outline" onClick={() => onEdit(workflow)}><Pencil className="mr-2 size-4" /> Edit</Button>
    <Button size="sm" variant="ghost" className="text-destructive" onClick={() => onArchive(workflow)}><Trash2 className="mr-2 size-4" /> Archive</Button>
  </div>
);

const EmptyTemplates: React.FC = () => (
  <Card className="border-dashed">
    <CardContent className="p-10 text-center">
      <h2 className="font-display text-lg font-bold">No workflow templates yet</h2>
      <p className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">
        Save a workflow as a template when the same kind of work is likely to come up again.
      </p>
    </CardContent>
  </Card>
);

const JobBoardTemplates: React.FC<Omit<TemplatesViewProps, 'concept'>> = (props) => {
  if (props.templates.length === 0) return <EmptyTemplates />;
  return (
    <div className="grid gap-4 xl:grid-cols-2">
      {props.templates.map((workflow) => (
        <Card key={workflow.id}>
          <CardHeader>
            <CardTitle className="text-xl">{workflow.name}</CardTitle>
            <CardDescription>{workflow.description || 'A reusable workflow template.'}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="mb-5 flex flex-wrap gap-2">
              <Badge variant="outline">{workflow.stage_count} crew {workflow.stage_count === 1 ? 'visit' : 'visits'}</Badge>
              <Badge variant="outline">{workflow.steps.length} assignments</Badge>
              <Badge variant="outline">Updated {formatDate(workflow.updated_at)}</Badge>
            </div>
            <div className="mb-5 space-y-2">
              {groupedTemplateStages(workflow).map(([stage, steps]) => (
                <div key={stage} className="rounded-xl border border-border bg-muted/40 px-4 py-3">
                  <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-muted-foreground">Crew visit {stage}</p>
                  <div className="mt-2 space-y-2">
                    {steps.map((step) => {
                      const tradeName = step.trade_name ?? 'No trade assigned';
                      return (
                        <div key={step.id} className="flex items-center justify-between gap-3">
                          <p className="min-w-0 text-sm font-medium">{step.title}</p>
                          <Badge
                            variant="outline"
                            className="shrink-0 bg-card"
                            aria-label={`Responsible trade: ${tradeName}`}
                          >
                            {tradeName}
                          </Badge>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
            <TemplateActions {...props} workflow={workflow} />
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

const CrewTemplates: React.FC<Omit<TemplatesViewProps, 'concept'>> = (props) => {
  if (props.templates.length === 0) return <EmptyTemplates />;
  return (
    <div className="space-y-4">
      {props.templates.map((workflow) => (
        <Card key={workflow.id}>
          <CardContent className="p-5">
            <div className="grid gap-5 lg:grid-cols-[250px_minmax(0,1fr)]">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-primary">Workflow template</p>
                <h3 className="mt-2 font-display text-xl font-bold">{workflow.name}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{workflow.description || 'Ready to use whenever this work comes up again.'}</p>
              </div>
              <div className="flex min-w-0 items-stretch gap-2 overflow-x-auto pb-2">
                {groupedTemplateStages(workflow).map(([stage, steps], index, groups) => (
                  <React.Fragment key={stage}>
                    <div className="min-w-[180px] rounded-xl border border-border bg-muted/40 p-3">
                      <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-muted-foreground">Crew visit {stage}</p>
                      <p className="mt-2 text-sm font-bold">{steps[0]?.responsible_trade_partner_name ?? steps[0]?.trade_name ?? 'Choose a crew'}</p>
                      <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">{steps.map((step) => step.title).join(' · ')}</p>
                    </div>
                    {index < groups.length - 1 && <ArrowRight className="mt-8 size-4 shrink-0 text-primary" />}
                  </React.Fragment>
                ))}
              </div>
            </div>
            <div className="mt-5 border-t border-border pt-4">
              <TemplateActions {...props} workflow={workflow} />
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

const SequenceTemplates: React.FC<Omit<TemplatesViewProps, 'concept'>> = (props) => {
  if (props.templates.length === 0) return <EmptyTemplates />;
  return (
    <div className="grid gap-4 xl:grid-cols-2">
      {props.templates.map((workflow) => (
        <Card key={workflow.id}>
          <CardHeader>
            <div className="flex items-start justify-between gap-4">
              <div>
                <CardTitle className="text-xl">{workflow.name}</CardTitle>
                <CardDescription className="mt-1">{workflow.description || 'Workflow template'}</CardDescription>
              </div>
              <Route className="size-5 text-primary" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="mb-5 space-y-0">
              {groupedTemplateStages(workflow).map(([stage, steps], index, groups) => (
                <div key={stage} className="grid grid-cols-[2rem_minmax(0,1fr)] gap-3">
                  <div className="flex flex-col items-center">
                    <span className="grid size-8 place-items-center rounded-full border border-primary/30 bg-primary/12 text-xs font-bold text-primary">{stage}</span>
                    {index < groups.length - 1 && <span className="my-1 h-full min-h-7 w-px bg-border" />}
                  </div>
                  <div className="pb-4">
                    <p className="text-sm font-bold">{steps.map((step) => step.title).join(' · ')}</p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {steps.map((step) => step.responsible_trade_partner_name ?? step.trade_name).filter(Boolean).join(', ') || 'Crew assigned when work begins'}
                    </p>
                  </div>
                </div>
              ))}
            </div>
            <TemplateActions {...props} workflow={workflow} />
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

export const WorkflowTemplatesConceptView: React.FC<TemplatesViewProps> = (props) => {
  if (props.concept === 'crew-handoffs') return <CrewTemplates {...props} />;
  if (props.concept === 'sequence-view') return <SequenceTemplates {...props} />;
  return <JobBoardTemplates {...props} />;
};
